// AppErrorBoundary — top-level boundary that catches render-time crashes
// in <App /> itself (e.g. duplicate-React-instance failures from a stale
// build, or any uncaught render error in the React tree).
//
// Behaviour on a caught error:
//   1. Always report to Sentry with classification.
//   2. Check /version.json — if a fresher build is live, OR the error
//      message matches a known stale-build pattern, attempt a one-shot
//      reload via the shared circuit breaker.
//   3. If the breaker has already tripped for this build, OR the crash
//      doesn't look stale-build-related, render the fallback UI.

import React, { Component, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import {
  currentBuildId,
  reloadForStaleBuild,
} from "./utils/reloadForStaleBuild";

declare const __APP_VERSION__: string;
declare const __COMMIT_SHA__: string;

const SUPPORT_EMAIL = "support@newsquawk.com";
const RELOAD_KEY = "app_crash_reload";
const VERSION_CHECK_TIMEOUT_MS = 1500;

const STALE_BUILD_PATTERNS: RegExp[] = [
  /Cannot read properties of null \(reading 'use[A-Z]\w*'\)/,
  /Invalid hook call/i,
  /ChunkLoadError/i,
  /Loading chunk \d+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Loading CSS chunk/i,
];

type CrashClass = "stale_build_suspected" | "unknown";

function classify(error: Error): CrashClass {
  const msg = `${error?.name ?? ""} ${error?.message ?? ""}`;
  for (const pattern of STALE_BUILD_PATTERNS) {
    if (pattern.test(msg)) return "stale_build_suspected";
  }
  return "unknown";
}

async function isFresherBuildLive(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(
      () => controller.abort(),
      VERSION_CHECK_TIMEOUT_MS
    );
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return false;
    const data = (await res.json()) as { commitSha?: string };
    return !!data.commitSha && data.commitSha !== __COMMIT_SHA__;
  } catch {
    return false;
  }
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  reloading: boolean;
  crashClass: CrashClass | null;
}

class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, reloading: false, crashClass: null };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true, reloading: false, crashClass: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const crashClass = classify(error);
    console.error(
      `[AppErrorBoundary] caught (${crashClass})`,
      error,
      errorInfo
    );
    this.setState({ crashClass });

    Sentry.withScope((scope) => {
      scope.setTag("error_type", "app_crash");
      scope.setTag("crash_class", crashClass);
      scope.setContext("react", {
        componentStack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    });

    void this.maybeAutoReload(error, crashClass);
  }

  private async maybeAutoReload(error: Error, crashClass: CrashClass) {
    const hasReloaded =
      sessionStorage.getItem(RELOAD_KEY) === currentBuildId;
    if (hasReloaded) return;

    // Only auto-reload when the page has had user interaction this navigation.
    // Without that, a silent reload kills any active media playback (squawk
    // audio etc.) AND Chrome's autoplay policy won't let it auto-resume
    // post-reload. Falling through to the fallback UI puts a Reload button
    // in front of the user — they click, the gesture is fresh on the new
    // page, audio can resume. Sentry capture (in componentDidCatch above)
    // still fires either way so we keep visibility into how often this
    // would have auto-reloaded.
    const hasActivation =
      typeof navigator !== "undefined" &&
      typeof navigator.userActivation !== "undefined" &&
      navigator.userActivation.hasBeenActive === true;
    if (!hasActivation) return;

    const fresher = await isFresherBuildLive();
    if (!fresher && crashClass === "unknown") return;

    this.setState({ reloading: true });
    const reloaded = reloadForStaleBuild({
      key: RELOAD_KEY,
      reason: "app_crash",
      error,
      context: {
        crash_class: crashClass,
        fresher_build_live: fresher,
      },
    });
    if (!reloaded) this.setState({ reloading: false });
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const buildLine =
      typeof __APP_VERSION__ !== "undefined" &&
      typeof __COMMIT_SHA__ !== "undefined"
        ? `v${__APP_VERSION__} · ${__COMMIT_SHA__.slice(0, 7)}`
        : "unknown build";
    const subject = encodeURIComponent(`App crash on ${buildLine}`);

    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950 px-4">
        <div className="w-full max-w-md text-center">
          {this.state.reloading ? (
            <>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Loading the latest version…
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                One moment.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                We couldn&rsquo;t load the latest version of Newsquawk.
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                A reload usually fixes this. If it keeps happening, get in
                touch and we&rsquo;ll look into it.
              </p>
              <div className="mt-5 flex items-center justify-center">
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Reload
                </button>
              </div>
              <p className="mt-6 text-xs text-gray-500 dark:text-gray-500">
                {buildLine} ·{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=${subject}`}
                  className="underline hover:text-gray-700 dark:hover:text-gray-300"
                >
                  {SUPPORT_EMAIL}
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
